import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Box, IconButton, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HighlightIcon from '@mui/icons-material/Highlight';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <IconButton
      size="small"
      onClick={onClick}
      title={title}
      sx={{
        borderRadius: 1,
        color: isActive ? 'primary.main' : 'text.secondary',
        backgroundColor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { backgroundColor: isActive ? 'action.focus' : 'action.hover' },
      }}
    >
      {children}
    </IconButton>
  );

  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 0.25, p: 0.75,
      borderBottom: '1px solid', borderColor: 'divider',
      backgroundColor: 'action.hover',
      borderTopLeftRadius: 8, borderTopRightRadius: 8,
    }}>
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
        <StrikethroughSIcon fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code Block">
        <CodeIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
        <FormatQuoteIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight().run()} isActive={editor.isActive('highlight')} title="Highlight">
        <HighlightIcon fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
        <UndoIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
        <RedoIcon fontSize="small" />
      </ToolbarButton>
    </Box>
  );
};

export const RichTextEditor = ({ content, onChange, placeholder = 'Write a description...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when prop changes (e.g., switching between edit tasks)
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content]);

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 1,
      '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px var(--mui-palette-primary-main, rgba(110,86,207,0.15))' },
      '& .tiptap': {
        outline: 'none', padding: '12px 16px', minHeight: 120, maxHeight: 300, overflowY: 'auto',
        fontSize: 14, lineHeight: 1.6, color: 'inherit',
        '& p.is-editor-empty:first-child::before': {
          content: 'attr(data-placeholder)', color: 'var(--mui-palette-text-disabled)', float: 'left', height: 0, pointerEvents: 'none',
        },
        '& ul, & ol': { paddingLeft: 24 },
        '& blockquote': { borderLeft: '3px solid var(--mui-palette-divider)', marginLeft: 0, paddingLeft: 16, color: 'var(--mui-palette-text-secondary)' },
        '& code': { backgroundColor: 'var(--mui-palette-action-hover)', borderRadius: 4, padding: '2px 4px', fontSize: 13 },
        '& pre': { backgroundColor: 'var(--mui-palette-text-primary)', color: 'var(--mui-palette-background-paper)', borderRadius: 8, padding: 16, overflowX: 'auto' },
        '& pre code': { backgroundColor: 'transparent', padding: 0, color: 'inherit' },
        '& mark': { backgroundColor: '#FEF08A', borderRadius: 2 },
      },
    }}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
};
